import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import {
  collection, query, orderBy, getDocs, doc, updateDoc, onSnapshot
} from "firebase/firestore";
import "./Admin.css";

// Only these emails can access admin
const ADMIN_EMAILS = ["neotericroots@gmail.com"];

const STATUS_OPTIONS = ["confirmed", "processing", "shipped", "delivered", "cancelled"];
const STATUS_COLORS = {
  confirmed:  { bg: "#EAF5E0", color: "#2D5016" },
  processing: { bg: "#FFF8EC", color: "#C8860A" },
  shipped:    { bg: "#E8F0FE", color: "#1A56DB" },
  delivered:  { bg: "#EAF5E0", color: "#166534" },
  cancelled:  { bg: "#FDE8E8", color: "#8B0000" },
};

export default function Admin() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [updating, setUpdating] = useState(null);
  const [trackingInputs, setTrackingInputs] = useState({});
  const [stats, setStats] = useState({ total: 0, revenue: 0, confirmed: 0, shipped: 0 });

  // Check admin access
  useEffect(() => {
    if (!currentUser) { navigate("/login"); return; }
    if (!ADMIN_EMAILS.includes(currentUser.email)) { navigate("/"); return; }
  }, [currentUser, navigate]);

  // Real-time orders listener
  useEffect(() => {
    if (!currentUser || !ADMIN_EMAILS.includes(currentUser.email)) return;

    const q = query(collection(db, "orders"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOrders(data);

      // Calculate stats
      const revenue = data.reduce((sum, o) => sum + (o.amount || 0), 0);
      const confirmed = data.filter(o => o.status === "confirmed").length;
      const shipped = data.filter(o => o.status === "shipped").length;
      setStats({ total: data.length, revenue, confirmed, shipped });
      setLoading(false);
    });

    return unsubscribe;
  }, [currentUser]);

  async function updateStatus(orderId, newStatus) {
    setUpdating(orderId);
    try {
      await updateDoc(doc(db, "orders", orderId), {
        status: newStatus,
        updatedAt: new Date()
      });
    } catch (err) {
      alert("Failed to update status");
    } finally {
      setUpdating(null);
    }
  }

  async function updateTracking(orderId) {
    const trackingId = trackingInputs[orderId];
    if (!trackingId) return;
    setUpdating(orderId);
    try {
      await updateDoc(doc(db, "orders", orderId), {
        trackingId,
        status: "shipped",
        updatedAt: new Date()
      });
      setTrackingInputs(prev => ({ ...prev, [orderId]: "" }));
    } catch (err) {
      alert("Failed to update tracking");
    } finally {
      setUpdating(null);
    }
  }

  // Filter and search
  const filtered = orders.filter(order => {
    const matchFilter = filter === "all" || order.status === filter;
    const matchSearch = !search ||
      order.orderId?.toLowerCase().includes(search.toLowerCase()) ||
      order.customerName?.toLowerCase().includes(search.toLowerCase()) ||
      order.customerEmail?.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  if (loading) return (
    <div className="admin-page">
      <div className="page-container">
        <div className="spinner"></div>
      </div>
    </div>
  );

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-header-inner">
          <div>
            <span className="section-label">Admin Panel</span>
            <h1>PureJar Orders Dashboard</h1>
          </div>
          <div className="admin-date">
            {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </div>
        </div>
      </div>

      <div className="page-container">

        {/* Stats */}
        <div className="admin-stats">
          <div className="stat-card">
            <span className="stat-icon">📦</span>
            <div>
              <strong>{stats.total}</strong>
              <p>Total Orders</p>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-icon">💰</span>
            <div>
              <strong>₹{stats.revenue.toLocaleString("en-IN")}</strong>
              <p>Total Revenue</p>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-icon">✅</span>
            <div>
              <strong>{stats.confirmed}</strong>
              <p>To Process</p>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-icon">🚚</span>
            <div>
              <strong>{stats.shipped}</strong>
              <p>Shipped</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="admin-controls">
          <div className="admin-filters">
            {["all", ...STATUS_OPTIONS].map(s => (
              <button
                key={s}
                className={`filter-btn ${filter === s ? "active" : ""}`}
                onClick={() => setFilter(s)}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
                {s !== "all" && (
                  <span className="filter-count">
                    {orders.filter(o => o.status === s).length}
                  </span>
                )}
              </button>
            ))}
          </div>
          <input
            className="admin-search"
            type="text"
            placeholder="Search by order ID, name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Orders */}
        {filtered.length === 0 ? (
          <div className="admin-empty">
            <span>📭</span>
            <p>No orders found</p>
          </div>
        ) : (
          <div className="admin-orders">
            {filtered.map(order => {
              const sc = STATUS_COLORS[order.status] || STATUS_COLORS.confirmed;
              return (
                <div key={order.id} className="admin-order-card card">
                  <div className="admin-order-top">
                    <div className="admin-order-id">
                      <p className="order-id-label">Order ID</p>
                      <strong>{order.orderId || order.id}</strong>
                      <p className="order-date">
                        {order.createdAt?.toDate?.()?.toLocaleDateString("en-IN", {
                          day: "numeric", month: "short", year: "numeric",
                          hour: "2-digit", minute: "2-digit"
                        }) || "—"}
                      </p>
                    </div>
                    <div className="admin-order-status">
                      <span className="status-badge" style={{ background: sc.bg, color: sc.color }}>
                        {order.status}
                      </span>
                      <select
                        className="status-select"
                        value={order.status}
                        onChange={e => updateStatus(order.id, e.target.value)}
                        disabled={updating === order.id}
                      >
                        {STATUS_OPTIONS.map(s => (
                          <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="admin-order-body">
                    {/* Customer */}
                    <div className="admin-section">
                      <h4>👤 Customer</h4>
                      <p><strong>{order.customerName}</strong></p>
                      <p>{order.customerEmail}</p>
                      <p>{order.customerPhone || "No phone"}</p>
                    </div>

                    {/* Address */}
                    <div className="admin-section">
                      <h4>📍 Delivery Address</h4>
                      {order.address ? (
                        <>
                          <p><strong>{order.address.name}</strong></p>
                          <p>{order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ""}</p>
                          <p>{order.address.city}, {order.address.state} — {order.address.pincode}</p>
                          <p>📱 {order.address.phone}</p>
                        </>
                      ) : <p>No address</p>}
                    </div>

                    {/* Products */}
                    <div className="admin-section">
                      <h4>🛒 Products</h4>
                      {(order.products || []).map((item, i) => (
                        <div key={i} className="admin-product-row">
                          <span>{item.icon}</span>
                          <span>{item.name}</span>
                          <span>×{item.quantity}</span>
                          <strong>₹{item.price * item.quantity}</strong>
                        </div>
                      ))}
                      <div className="admin-total">Total: <strong>₹{order.amount}</strong></div>
                    </div>

                    {/* Payment */}
                    <div className="admin-section">
                      <h4>💳 Payment</h4>
                      <p className="payment-id">ID: {order.razorpayPaymentId || "—"}</p>
                      {order.trackingId && (
                        <p className="tracking-id">🚚 Tracking: <strong>{order.trackingId}</strong></p>
                      )}
                      <div className="tracking-input-row">
                        <input
                          type="text"
                          placeholder="Enter Shiprocket AWB..."
                          value={trackingInputs[order.id] || ""}
                          onChange={e => setTrackingInputs(prev => ({ ...prev, [order.id]: e.target.value }))}
                        />
                        <button
                          className="btn-primary"
                          style={{ padding: "8px 16px", fontSize: "0.78rem" }}
                          onClick={() => updateTracking(order.id)}
                          disabled={updating === order.id || !trackingInputs[order.id]}
                        >
                          {updating === order.id ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
